import { ApiClient } from "@anna-money/anna-web-lib/api";
import { type NavigateFunction } from "react-router-dom";
import { Memoize } from "typescript-memoize";
import { AbnSoleTraderRegisterFormStepsStore } from "services/abnSoleTraderRegisterForm/abnSoleTraderRegisterFormStepsStore";
import { AbnSoleTraderRegisterFormStore } from "services/abnSoleTraderRegisterForm/abnSoleTraderRegisterFormStore";
import { CompanyStateFormStore } from "services/companyStateForm/companyStateFormStore";
import { DirectorFormStore } from "services/directorForm/au/directorFormStore";
import { MainBusinessActivityFormStore } from "services/mainBusinessActivityFormStore/mainBusinessActivityFormStore";
import { DirectorAbnQuestionsStore } from "services/questions/au/abn/directorAbnQuestionsStore";
import { BasketQuestionsStore } from "services/questions/au/basketQuestionsStore";
import { CompanyNameAcnQuestionsStore } from "services/questions/au/companyNameAcnQuestionStore";
import { DirectorShareQuestionsStore } from "services/questions/au/directorShareQuestionsStore";
import { AbnSoleTraderQuestionsStore } from "services/questions/au/soleTrader/abnSoleTraderQuestionsStore";
import { DirectorSoleTraderQuestionsStore } from "services/questions/au/soleTrader/directorSoleTraderQuestionsStore";
import { CompanyAbnStepStore } from "services/stepStore/au/abn/companyAbnStepStore";
import { CompanyNameAcnStepStore } from "services/stepStore/au/acn/companyNameAcnStepStore";
import { DirectorAcnStepStore } from "services/stepStore/au/acn/directorAcnStepStore";
import { AbnSoleTraderStepStore } from "services/stepStore/au/soleTrader/abnSoleTraderStepStore";
import { SuburbSearchClient } from "services/suburbSearch/suburbSearchClient";
import { SuburbSearchStore } from "services/suburbSearch/suburbSearchStore";
import { AuthenticationHandler } from "./auth/authenticationApiHandler";
import { BaseServices, Services } from "./services";
import { AbnRegisterFormStepsStore } from "./services/abnRegisterForm/abnRegisterFormStepsStore";
import { AbnRegisterFormStore } from "./services/abnRegisterForm/abnRegisterFormStore";
import { AcnRegisterFormStepsStore } from "./services/acnRegisterForm/acnRegisterFormStepsStore";
import { AcnRegisterFormStore } from "./services/acnRegisterForm/acnRegisterFormStore";
import { BillingItemsStore } from "./services/billingItems/au/billingItemsStore";
import { BusinessActivitiesSelectStore } from "./services/businessActivitiesSelect/au/businessActivitiesSelectStore";
import { CheckoutBasketStore } from "./services/checkoutBasket/au/checkoutBasketStore";
import { GstCompanyStore } from "./services/company/gstCompanyStore";
import { CompanyAddressStore } from "./services/companyAddress/au/companyAddressStore";
import { CompanyAddressFormStore } from "./services/companyAddressForm/au/companyAddressFormStore";
import { CompanyBusinessActivitiesStore } from "./services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import { CompanyNameFormStore } from "./services/companyNameForm/au/companyNameFormStore";
import { FeatureStore } from "./services/feature/featureStore";
import { PricingPlanStore } from "./services/pricingPlan/au/pricingPlanStore";
import { PricingPlanClient } from "./services/pricingPlan/pricingPlanClient";
import { PricingPlanFormStore } from "./services/pricingPlanForm/au/pricingPlanFormStore";
import { ProfileStore } from "./services/profile/profileStore";
import { CompanyAbnQuestionsStore } from "./services/questions/au/abn/companyAbnQuestionsStore";
import { AbnQuestionsStore } from "./services/questions/au/abnQuestionsStore";
import { CompanyAcnQuestionsStore } from "./services/questions/au/companyAcnQuestionsStore";
import { DirectorQuestionsStore } from "./services/questions/au/directorQuestionsStore";
import { IdentityQuestionsStore } from "./services/questions/au/identityQuestionsStore";
import { SoleTraderDecisionQuestionsStore } from "./services/questions/au/soleTraderDecisionQuestionsStore";
import { ScaSignupClient } from "./services/scaSignup/scaSignupClient";
import { ScaSignupStore } from "./services/scaSignup/scaSignupStore";
import { SignUpPageStore } from "./services/signUpPage/au/signUpPageStore";
import { SoleTraderQuestionnaireClient } from "./services/soleTraderQuestionnaire/soleTraderQuestionnaireClient";
import { SoleTraderQuestionnaireStore } from "./services/soleTraderQuestionnaire/soleTraderQuestionnaireStore";
import { AbnStepStore } from "./services/stepStore/au/acn/abnStepStore";
import { CompanyAcnStepStore } from "./services/stepStore/au/acn/companyAcnStepStore";
import { IdentityStepStore } from "./services/stepStore/au/identityStepStore";
import type { UserClient } from "./services/user/userClient";
import { UserStore } from "./services/user/userStore";

export class ServicesAu extends Services {
  constructor(
    protected readonly _baseServices: BaseServices,
    readonly _authenticationHandler: AuthenticationHandler,
    readonly _apiClient: ApiClient,
    readonly _userClient: UserClient,
    readonly _userStore: UserStore,
    readonly _profileStore: ProfileStore,
    readonly _featureStore: FeatureStore,
    readonly _navigate: NavigateFunction,
  ) {
    super(
      _baseServices,
      _authenticationHandler,
      _apiClient,
      _userClient,
      _userStore,
      _profileStore,
      _featureStore,
      _navigate,
    );
  }

  @Memoize()
  get abnStepStore(): AbnStepStore {
    return new AbnStepStore(this.companyStore, this.companyBusinessActivitiesStore, this.gstCompanyStore);
  }

  @Memoize()
  get abnQuestionsStore(): AbnQuestionsStore {
    return new AbnQuestionsStore(this.companyStore, this.issuesStore);
  }

  @Memoize()
  get gstCompanyStore(): GstCompanyStore {
    return new GstCompanyStore(this.companyStore, this.companyClient);
  }

  @Memoize()
  get companyAddressStore(): CompanyAddressStore {
    return new CompanyAddressStore(
      this.companyStore,
      this.addressClient,
      this.companyProductsStore,
      this.pricingPlanStore,
    );
  }

  @Memoize()
  get scaSignupStore(): ScaSignupStore {
    return new ScaSignupStore(
      new ScaSignupClient(this.apiClient),
      this.directorStore,
      this.companyAddressStore,
      this.profileStore,
    );
  }

  @Memoize()
  get companyAcnStepStore(): CompanyAcnStepStore {
    return new CompanyAcnStepStore(
      this.companyStore,
      this.companyAddressStore,
      this.companyProductsStore,
      this.pricingPlanStore,
      this.createAsicAgentSignatureStore(),
    );
  }

  @Memoize()
  get companyNameAcnStepStore(): CompanyNameAcnStepStore {
    return new CompanyNameAcnStepStore(this.companyStore);
  }

  @Memoize()
  get directorAcnStepStore(): DirectorAcnStepStore {
    return new DirectorAcnStepStore(this.directorStore, this.companyStore);
  }

  @Memoize()
  get companyAbnStepStore(): CompanyAbnStepStore {
    return new CompanyAbnStepStore(this.companyStore, this.companyBusinessActivitiesStore, this.gstCompanyStore);
  }

  @Memoize()
  get identityStepStore(): IdentityStepStore {
    return new IdentityStepStore(this.directorStore, this.sumsubApplicationStore);
  }

  @Memoize()
  get companyBusinessActivitiesStore(): CompanyBusinessActivitiesStore {
    return new CompanyBusinessActivitiesStore(this.companyStore, this.addressClient);
  }

  @Memoize()
  get billingItemsStore(): BillingItemsStore {
    return new BillingItemsStore();
  }

  @Memoize()
  get pricingPlanStore(): PricingPlanStore {
    return new PricingPlanStore(
      this.companyStore,
      this.companyProductsStore,
      new PricingPlanClient(this.apiClient),
      this.analyticsManager,
    );
  }

  @Memoize()
  get companyNameAcnQuestionStore(): CompanyNameAcnQuestionsStore {
    return new CompanyNameAcnQuestionsStore(this.companyStore, this.issuesStore);
  }

  @Memoize()
  get companyAcnQuestionsStore(): CompanyAcnQuestionsStore {
    return new CompanyAcnQuestionsStore(
      this.companyStore,
      this.companyAddressStore,
      this.issuesStore,
      this.featureStore,
    );
  }

  @Memoize()
  get companyAbnQuestionsStore(): CompanyAbnQuestionsStore {
    return new CompanyAbnQuestionsStore(this.companyStore, this.issuesStore);
  }

  @Memoize()
  get directorQuestionsStore(): DirectorQuestionsStore {
    return new DirectorQuestionsStore(
      this.directorStore,
      this.directorAddressStore,
      this.companyStore,
      this.issuesStore,
    );
  }

  @Memoize()
  get directorShareQuestionStore(): DirectorShareQuestionsStore {
    return new DirectorShareQuestionsStore(this.issuesStore);
  }

  @Memoize()
  get directorAbnQuestionsStore(): DirectorAbnQuestionsStore {
    return new DirectorAbnQuestionsStore(this.directorStore, this.directorAddressStore, this.issuesStore);
  }

  @Memoize()
  get identityQuestionsStore(): IdentityQuestionsStore {
    return new IdentityQuestionsStore(
      this.addressVerificationStore,
      this.sumsubApplicationStore,
      this.analyticsManager,
      this.issuesStore,
    );
  }

  @Memoize()
  get basketQuestionsStore(): BasketQuestionsStore {
    return new BasketQuestionsStore(this.issuesStore);
  }

  @Memoize()
  get soleTraderDecisionQuestionsStore(): SoleTraderDecisionQuestionsStore {
    return new SoleTraderDecisionQuestionsStore(this.issuesStore);
  }

  @Memoize()
  get directorSoleTraderQuestionsStore(): DirectorSoleTraderQuestionsStore {
    return new DirectorSoleTraderQuestionsStore(this.directorStore, this.directorAddressStore, this.issuesStore);
  }

  @Memoize()
  get abnSoleTraderQuestionsStore(): AbnSoleTraderQuestionsStore {
    return new AbnSoleTraderQuestionsStore(this.issuesStore);
  }

  @Memoize()
  get abnSoleTraderStepStore(): AbnSoleTraderStepStore {
    return new AbnSoleTraderStepStore(
      this.companyStore,
      this.companyBusinessActivitiesStore,
      this.abnSoleTraderQuestionsStore,
    );
  }

  @Memoize()
  get suburbSearchStore(): SuburbSearchStore {
    return new SuburbSearchStore(new SuburbSearchClient(this.apiClient));
  }

  @Memoize()
  get soleTraderQuestionnaireClient(): SoleTraderQuestionnaireClient {
    return new SoleTraderQuestionnaireClient(this.apiClient);
  }

  @Memoize()
  get soleTraderQuestionnaireStore(): SoleTraderQuestionnaireStore {
    return new SoleTraderQuestionnaireStore(this.soleTraderQuestionnaireClient);
  }

  @Memoize()
  get acnRegisterFormFormStepsStore(): AcnRegisterFormStepsStore {
    return new AcnRegisterFormStepsStore(
      this.companyAcnQuestionsStore,
      this.abnQuestionsStore,
      this.directorQuestionsStore,
      this.directorShareQuestionStore,
      this.identityQuestionsStore,
      this.analyticsManager,
      this.companyNameAcnQuestionStore,
    );
  }

  @Memoize()
  get abnRegisterFormFormStepsStore(): AbnRegisterFormStepsStore {
    return new AbnRegisterFormStepsStore(
      this.companyAbnQuestionsStore,
      this.directorAbnQuestionsStore,
      this.identityQuestionsStore,
      this.analyticsManager,
    );
  }

  @Memoize()
  get abnSoleTraderRegisterFormStepsStore(): AbnSoleTraderRegisterFormStepsStore {
    return new AbnSoleTraderRegisterFormStepsStore(
      this.analyticsManager,
      this.soleTraderDecisionQuestionsStore,
      this.directorSoleTraderQuestionsStore,
      this.abnSoleTraderQuestionsStore,
      this.identityQuestionsStore,
    );
  }

  createCheckoutBasketStore = (): CheckoutBasketStore =>
    new CheckoutBasketStore(this.companyStore, this.companyProductsStore, this.pricingPlanStore);

  createCompanyAddressFormStore = (): CompanyAddressFormStore => new CompanyAddressFormStore(this.companyAddressStore);

  createCompanyNameFormStore = (): CompanyNameFormStore => new CompanyNameFormStore(this.companyStore);

  createCompanyStateFormStore = (): CompanyStateFormStore => new CompanyStateFormStore(this.companyStore);

  createCompanyBusinessActivitiesStore = (): MainBusinessActivityFormStore =>
    new MainBusinessActivityFormStore(this.companyBusinessActivitiesStore);

  createPricingPlanFormStore = (): PricingPlanFormStore => {
    return new PricingPlanFormStore(this.pricingPlanStore);
  };

  createDirectorFormStore = (directorId?: string): DirectorFormStore => {
    return new DirectorFormStore(
      this.directorStore,
      this.companyStore,
      this.directorAddressStore,
      this.directorPositionsStore,
      directorId,
    );
  };

  createAcnRegisterFormStore = (): AcnRegisterFormStore => {
    return new AcnRegisterFormStore(
      this.directorStore,
      this.companyStore,
      this.companyAddressStore,
      this.companyProductsStore,
      this.paymentMethodsStore,
      this.consentStore,
      this.companyBusinessActivitiesStore,
      this.businessActivitiesStore,
      this.pricingPlanStore,
      this.userStore,
      this.sumsubApplicationStore,
      this.analyticsManager,
      this.acnRegisterFormFormStepsStore,
      this.incorporationStore,
      this.issuesStore,
      this.companyAcnStepStore,
      this.identityStepStore,
      this.abnStepStore,
      this.countryStore,
      this.companyNameAcnStepStore,
      this.directorAcnStepStore,
      this.featureStore,
      this.billingControllerStore,
    );
  };

  createAbnRegisterFormStore = (): AbnRegisterFormStore => {
    return new AbnRegisterFormStore(
      this.directorStore,
      this.companyStore,
      this.paymentMethodsStore,
      this.consentStore,
      this.companyBusinessActivitiesStore,
      this.businessActivitiesStore,
      this.userStore,
      this.sumsubApplicationStore,
      this.analyticsManager,
      this.abnRegisterFormFormStepsStore,
      this.incorporationStore,
      this.issuesStore,
      this.companyAbnStepStore,
      this.identityStepStore,
      this.countryStore,
    );
  };

  createAbnSoleTraderRegisterFormStore = (): AbnSoleTraderRegisterFormStore => {
    return new AbnSoleTraderRegisterFormStore(
      this.directorStore,
      this.companyStore,
      this.paymentMethodsStore,
      this.consentStore,
      this.companyBusinessActivitiesStore,
      this.businessActivitiesStore,
      this.userStore,
      this.sumsubApplicationStore,
      this.analyticsManager,
      this.abnSoleTraderRegisterFormStepsStore,
      this.incorporationStore,
      this.issuesStore,
      this.companyAbnStepStore,
      this.identityStepStore,
      this.countryStore,
      this.soleTraderDecisionQuestionsStore,
      this.directorAddressStore,
      this.abnSoleTraderStepStore,
      this.featureStore,
      this.billingControllerStore,
    );
  };

  createSignUpPageStore = (): SignUpPageStore =>
    new SignUpPageStore(
      this.companyClient,
      this.leadClient,
      this.analyticsManager,
      this.userStore,
      this._navigate,
      this.config,
    );

  createBusinessActivitiesSelectStore = (): BusinessActivitiesSelectStore =>
    new BusinessActivitiesSelectStore(this.businessActivitiesClient, this.businessActivitiesStore, this.companyStore);
}
